import { formatRelative } from '@erp-boilerplate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { queryKeys } from '@/lib/query-client';
import { notificationsService } from '@/services/notifications.service';

/**
 * The bell in the top bar.
 *
 * ---------------------------------------------------------------------------
 * IT COUNTS YOUR OWN MAIL, NOT THE ORGANISATION'S
 * ---------------------------------------------------------------------------
 *
 * The badge is the number of notifications addressed to *this account* and not
 * yet read. It is emphatically not the tenant's unread customer notifications —
 * those are messages sent to customers, they are not addressed to any member of
 * staff, and a badge counting them would show every operator the same permanent
 * unreadable number.
 *
 * In this build nothing is addressed to staff automatically, so most accounts
 * will see no badge. That is correct rather than broken, and the dropdown says
 * so instead of showing an unexplained empty list.
 *
 * ---------------------------------------------------------------------------
 * NO POLLING
 * ---------------------------------------------------------------------------
 *
 * The count is fetched on mount and refetched when something invalidates it.
 * A timer would put a request per minute per open tab against an API for a
 * number that, in a system with no staff-addressed notifications, changes
 * approximately never. When real-time matters, the answer is a subscription,
 * not a poll — and neither is in scope for this phase.
 */
export const NotificationBell = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const unread = useQuery({
    queryKey: queryKeys.notifications.unread,
    queryFn: () => notificationsService.unreadCount(),
    staleTime: 60_000,
  });

  const mine = useQuery({
    queryKey: queryKeys.notifications.list({ mine: 'true', pageSize: 5 }),
    queryFn: () => notificationsService.list({ mine: 'true', pageSize: 5 }),
    staleTime: 60_000,
  });

  const markAllRead = useMutation({
    mutationFn: () => notificationsService.markAllRead(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });

  const count = unread.data?.unread ?? 0;
  const items = mine.data?.items ?? [];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          className="relative text-topbar-muted hover:bg-topbar-foreground/10 hover:text-topbar-foreground"
          aria-label={count > 0 ? `Notifications, ${count} unread` : 'Notifications'}
        >
          <Bell />
          {count > 0 ? (
            <span className="absolute -right-0.5 -top-0.5 flex size-4 items-center justify-center rounded-full bg-destructive text-[10px] font-medium text-destructive-foreground">
              {count > 9 ? '9+' : count}
            </span>
          ) : null}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center justify-between gap-2">
          <span>Your notifications</span>
          {count > 0 ? (
            <button
              type="button"
              className="text-2xs font-normal text-muted-foreground hover:text-foreground"
              onClick={() => markAllRead.mutate()}
            >
              Mark all read
            </button>
          ) : null}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {items.length === 0 ? (
          <div className="px-2 py-4 text-center">
            <p className="text-xs text-muted-foreground">Nothing addressed to you.</p>
            <p className="mt-1 text-2xs text-muted-foreground">
              Messages the showroom sends to customers appear in the notification centre.
            </p>
          </div>
        ) : (
          items.map((item) => (
            <DropdownMenuItem
              key={item.id}
              className="flex flex-col items-start gap-0.5"
              onSelect={() => navigate(`/notifications/${item.id}`)}
            >
              <span className="w-full truncate text-xs font-medium">{item.title}</span>
              <span className="text-2xs text-muted-foreground">
                {item.eventLabel} · {formatRelative(item.createdAt)}
              </span>
            </DropdownMenuItem>
          ))
        )}

        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate('/notifications')}>
          <span className="text-xs">Open the notification centre</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
